import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Printer, ArrowLeft, Download } from 'lucide-react';
import { useDispatch, useSelector } from "react-redux";

import { getRateQuoteByID, createRateQuote, updateRateQuote } from '@/redux/RateQuote/actions';
import {
    getCountryList, getDocumentTypePurchaseList, getDocumentTypeSalesList, getIncoTermsList, getQuantityTypeList, getSalesPersonList, getUomTypeList, getCommodityGroup, getContainerType, GetOperator, getMoveType, GetCurrencyList, GetChargeBasisList, getUomList,
    getFileManagerList, getMasterCompanyDetails,
    getClauselist
} from "@/redux/Common/actions";

import moment from 'moment';
import { loaderStart, loaderStop } from '@/redux/Loader/actions';
import PrintHeader from '@/components/PrintHeader/PrintHeader';

/** Action button color (kept for the Print button only) */
const BOOKING_PRINT_HEADER_BG = '#1e4d8c';

/** Classic document section band — light gray with black text (matches reference image) */
const SECTION_HEADER_BG = '#d9d9d9';
const SECTION_HEADER_TEXT = '#000000';

const RateQuotePrint = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id: encodedId } = useParams();
    const idQuote = atob(encodedId); // Decode the base64 encoded ID

    const [quoteData, setQuoteData] = useState(null);
    const [formData, setFormData] = useState();
    const [clauses, setClauses] = useState();
    const [authUser] = JSON.parse(localStorage.getItem('authUser') ?? "[{}]");

    const { countryList, documentTypePurchaseList, documentTypeSalesList, incoTermsList, quantityTypeList, salesPersonList, uomTypeList, commodityGroupList, containerTypeList, operatorList, moveTypeList, MasterCompanyDetails } = useSelector((state) => state.common);
    const { loader } = useSelector((state) => state.loader);


    useEffect(() => {
        if (location.state?.quote) {
            setQuoteData(location.state.quote);
        } else {
            setQuoteData({
                id: 'RQE100167',
                customer: 'FAISAL JASSIM TRADING COMPANY',
                customerAddress: 'NEEDHAM ROAD IND EST\nDUBAI\nUNITED ARAB EMIRATES',
                customerPhone: '+971 4 123 4567',
                customerEmail: 'info@fjtrading.com',
                createdBy: 'MATT.IGS',
                salesPerson: 'MATT.IGS',
                quoteDate: '08 JAN 2026',
                effectiveDate: '08 JAN 2026',
                expiryDate: '31 JAN 2026',
                pol: 'NORFOLK',
                polCode: 'USORF',
                pod: 'DUBAI',
                podCode: 'AEDXB',
                origin: 'NORFOLK, VA',
                destination: 'DUBAI',
                moveType: 'PORT TO PORT',
                commodity: 'GENERAL CARGO',
                carrier: 'HAPAG-LLOYD',
                containerType: '1 X 20 STD',
                currency: 'USD',
                status: 'active',
                charges: [
                    { description: 'CUSTOMS/DOCS/VGM', currency: 'USD', basis: 'per cntr', amount: 125.00 },
                    { description: 'PRE-CARRIAGE', currency: 'USD', basis: 'per cntr', amount: 285.00 },
                    { description: 'OCEANFREIGHT', currency: 'USD', basis: 'per cntr', amount: 1950.00 },
                ],
                remarks: 'BASED ON GENERAL CARGO.\nOCEANFREIGHT IS INCLUSIVE OF BUNKER, ISPS, TERMINAL, PORT, LOW SULFUR AND ENERGY SURCHARGE.'
            });
        }
    }, [location.state]);

    useEffect(() => {
        if (idQuote != 0 && idQuote != null && idQuote != undefined) {
            loadCommonData();
        }
    }, [idQuote]);


    const loadCommonData = async () => {
        try {
            await dispatch(loaderStart());
            await dispatch(getCountryList());
            await dispatch(getSalesPersonList());
            await dispatch(GetCurrencyList());
            if (authUser?.companyId) {
                await dispatch(getMasterCompanyDetails(authUser.companyId));
            }
            await _getFormData();
        } catch (err) {
            console.warn("Some common data failed to load:", err);
        } finally {
            await dispatch(loaderStop());
        }
    };

    const mergeAllInCharges = (data) => {
        if (!data?.CarrierDetails) return data;
        const updatedCarriers = data.CarrierDetails.map((carrier) => {
            const charges = carrier?.rateCharges || [];
            const allInCharges = charges.filter(c => c.all_in);
            const normalCharges = charges.filter(c => !c.all_in);
            if (allInCharges.length === 0) return carrier;
            const grouped = allInCharges.reduce((acc, item) => {
                const key = item.basis || "UNKNOWN";
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
            }, {});
            const keys = [
                "set1sellrate", "set1buyrate",
                "set2sellrate", "set2buyrate",
                "set3sellrate", "set3buyrate"
            ];
            const mergedCharges = Object.values(grouped).map(group => {
                const base = group[0];
                const merged = {
                    ...base,
                    chargedescription: `Ocean Freight (All-In)`,
                };
                keys.forEach(key => {
                    const total = group.reduce((sum, item) => sum + (item[key] || 0), 0);
                    merged[key] = total; // or total if you don't want average
                });
                return merged;
            });
            return {
                ...carrier,
                rateCharges: [...normalCharges, ...mergedCharges],
            };
        });
        return {
            ...data,
            CarrierDetails: updatedCarriers,
        };
    };

    const _getFormData = async () => {
        try {
            const resultData = await dispatch(getRateQuoteByID(idQuote));
            const processedData = mergeAllInCharges(resultData);
            setFormData(processedData);
            getClauselistData(processedData?.rateQuoteClauses || []);
        } catch (err) {
            console.error("Failed to load rate quote:", err);
        }
    }

    const handlePrint = () => {
        window.print();
    };

    const handleDownload = () => {
        window.print();
    };

    const getClauselistData = async (formClauses = []) => {
        try {
            const payload = {
                IPageNo: 1,
                IPageRecords: 1000,
                Type: 3,
            };
            const clauseData = await dispatch(getClauselist(payload));

            const transformedData = formClauses.map((item) => {
                const matchedClause = clauseData?.find(
                    (c) => c.ClauseTypeDescriptionID === item.clauseTypeDescID,
                );

                return {
                    ClauseTypeDescriptionID: matchedClause?.ClauseTypeDescriptionID,
                    ClauseType: matchedClause?.ClauseType,
                    ClauseDescription: matchedClause.ClauseDescription,
                    ClausesCategoryType: matchedClause?.ClausesCategoryType,
                    ModuleType: matchedClause?.ModuleType,
                };
            });

            setClauses(transformedData);
        } catch (error) {
            console.error("Error loading clauses:", error);
        }
    };

    if (!quoteData) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading quotation...</p>
                </div>
            </div>
        );
    }

    const subtotal = formData?.charges?.reduce((sum, charge) => sum + charge.amount, 0) || 0;
    const grandTotal = subtotal;
    const currency = formData?.currency || 'USD';

    const SalesPersonName = (id) => {
        return salesPersonList.find(item => item.salesPersonId === id)?.salesPerson || "N/A";
    };



    return (
        <div className="min-h-screen bg-gray-200 print:bg-white">
            {/* Action Bar - Hidden when printing */}
            <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-center">
                    {/* <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all font-medium"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Rate Quotes
                    </button> */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleDownload}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300"
                        >
                            <Download className="w-4 h-4" />
                            Download PDF
                        </button>
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-5 py-2 text-white rounded-lg transition-all font-medium hover:opacity-90"
                            style={{ backgroundColor: BOOKING_PRINT_HEADER_BG }}
                        >
                            <Printer className="w-4 h-4" />
                            Print
                        </button>
                    </div>
                </div>
            </div>

            {/* A4 Print Container */}
            <div className="flex justify-center py-6 print:py-6">
                <div
                    className="a4-page bg-white shadow-xl print:shadow-none"
                    style={{ width: '210mm', maxWidth: '100%', minHeight: '297mm' }}
                >

                    {/* Wrap everything in a table so thead repeats on every printed page */}
                    <table
                        className="rate-quote-print-shell"
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                    >

                        {/* Custom Print Header — Logo (left) | RATE QUOTATION (center) | Address (right) */}
                        <thead className="print:table-header-group">
                            <tr>
                                <th
                                    colSpan={100}
                                    style={{ border: 'none', fontWeight: 'normal', width: '100%' }}
                                >
                                    <div className="rate-quote-print-doc-header bg-white w-full">
                                        <div className="px-6 pt-4 pb-3 flex justify-between items-center gap-6 w-full">
                                            {/* Logo */}
                                            <div className="flex items-center flex-1">
                                                <img
                                                    src={MasterCompanyDetails?.[0]?.CompanyLogoDark || "/shipez-logo.png"}
                                                    alt={MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"}
                                                    className="rate-quote-print-header-logo h-12 w-auto object-contain"
                                                />
                                            </div>

                                            {/* Centered Title */}
                                            <div className="flex-1 text-center">
                                                <h1 className="rate-quote-print-doc-title m-0 font-bold text-[20px] tracking-wide text-black">
                                                    RATE QUOTATION
                                                </h1>
                                            </div>

                                            {/* Address */}
                                            <div className="flex-1 text-right text-[11px] text-gray-800 leading-snug font-semibold">
                                                    {/* {MasterCompanyDetails?.[0]?.CompanyName && (
                                                        <p className="font-bold text-black">{MasterCompanyDetails?.[0]?.CompanyName}</p>
                                                    )} */}
                                                {MasterCompanyDetails?.[0]?.ProfileAddressPrint
                                                    ?.split(",")
                                                    ?.map((line, index) => (
                                                        <p key={index} className="font-normal text-gray-800">{line.trim()}</p>
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ height: '4px' }} />
                                </th>
                            </tr>
                        </thead>

                        {/* All page content goes in tbody */}
                        <tbody>
                            <tr>
                                <td>
                                    {/* Content */}
                                    <div className="print-content px-6 py-2 print:px-6 print:py-2">
                                        {/* Customer & Quote Details Row */}
                                        <div className="grid grid-cols-1 gap-0 mb-3 border border-gray-400">
                                            <div
                                                className="rate-quote-print-section-head px-3 py-1.5 text-left"
                                                style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}
                                            >
                                                <h3 className="m-0 font-bold text-[11px] uppercase tracking-wide text-black">Customer</h3>
                                            </div>
                                            <div className="grid grid-cols-2 gap-0">
                                                <div className="px-2 py-0.5 text-xs">
                                                    <p className="font-semibold text-gray-900">{formData?.customerName}</p>
                                                    <p className="text-gray-700 whitespace-pre-line text-xs mt-1">{formData?.customerAddress}</p>
                                                    <p className="text-gray-700 text-xs">TEL: {formData?.customerTel}</p>
                                                    <p className="text-gray-700 text-xs">{formData?.customerEmail}</p>
                                                </div>
                                                <div className="px-2 py-0.5 border-l border-gray-400 ">
                                                    <table className="w-full text-[10px]">
                                                        <tbody>
                                                            <tr>
                                                                <td className="py-0.5 text-black font-bold">RQ NUMBER.</td>
                                                                <td className="py-0.5 text-gray-900">: {formData?.rateQuoteNumber}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="py-0.5 text-black font-bold">QUOTE DATE</td>
                                                                <td className="py-0.5 text-gray-900">: {formData?.createdOn ? moment(formData?.createdOn).format('DD MMM YYYY') : ''}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="py-0.5 text-black font-bold">EFFECTIVE DATE</td>
                                                                <td className="py-0.5 text-gray-900">: {formData?.effectiveDate ? moment(formData?.effectiveDate).format('DD MMM YYYY') : ''}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="py-0.5 text-black font-bold">EXPIRY DATE</td>
                                                                <td className="py-0.5 text-gray-900">: {formData?.valideDate ? moment(formData?.valideDate).format('DD MMM YYYY') : ''}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="py-0.5 text-black font-bold">QUOTED BY</td>
                                                                <td className="py-0.5 text-gray-900">: {formData?.quotedBy}</td>
                                                            </tr>
                                                            <tr>
                                                                <td className="py-0.5 text-black font-bold">SALES PERSON</td>
                                                                <td className="py-0.5 text-gray-900">: {SalesPersonName(formData?.salesPersonSno)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>


                                        {/* Routing Instruction */}
                                        <div className="mb-3 border border-gray-400">
                                            <div className="grid grid-cols-1">
                                                <div
                                                    className="rate-quote-print-section-head px-3 py-1.5 text-left"
                                                    style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}
                                                >
                                                    <h3 className="m-0 font-bold text-[11px] uppercase tracking-wide text-black">
                                                        Routing Instruction
                                                    </h3>
                                                </div>
                                                <div className="grid grid-cols-2">
                                                    <div className="px-2 py-0.5">
                                                        <table className="w-full text-xs">
                                                            <tbody className='text-[10px]'>
                                                                <tr>
                                                                    <td className="py-1 text-black font-bold w-28">
                                                                        MOVE TYPE
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.moveType}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-1 text-black font-bold w-28">
                                                                        PLACE OF RECEIPT
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.origin}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-1 text-black font-bold w-28">
                                                                        PORT OF LOADING
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.portOfLoad}
                                                                        </span>
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="py-1 text-black font-bold">
                                                                        TRANSIT TIME
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.transitTime
                                                                                ? `${formData?.transitTime} days`
                                                                                : "-"}
                                                                        </span>
                                                                    </td>
                                                                </tr>

                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    <div className="px-2 py-0.5 border-l border-gray-400">
                                                        <table className="w-full text-[10px]">
                                                            <tbody>

                                                                <tr>
                                                                    <td className="py-1 text-black font-bold">
                                                                        PORT OF DISCHARGE

                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.portOfDischarge ?? "-"
                                                                            }
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-1 text-black font-bold">
                                                                        PLACE OF DELIVERY
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.destination ?? "-"
                                                                            }
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td className="py-1 text-black font-bold">
                                                                        LOADING LOCATION
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.loadingLoc ?? "-"
                                                                            }
                                                                        </span>
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="py-1 text-black font-bold">
                                                                        COMMODITY
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.commodity}
                                                                        </span>
                                                                    </td>
                                                                </tr>


                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="hidden mb-3 border border-gray-400">
                                            <div className="grid grid-cols-1">
                                                {/* Header */}
                                                <div
                                                    className="rate-quote-print-section-head px-3 py-1.5 text-left"
                                                    style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}
                                                >
                                                    <h3 className="m-0 font-bold text-[11px] uppercase tracking-wide text-black">
                                                        Container & Commodity
                                                    </h3>
                                                </div>
                                                <div className="grid grid-cols-2">
                                                    {/* LEFT SIDE */}
                                                    <div className="px-2 py-0.5">
                                                        <table className="w-full text-[10px]">
                                                            <tbody>
                                                                <tr>
                                                                    <td className="py-1 text-black font-bold w-32">
                                                                        COMMODITY
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.commodity || "-"}
                                                                        </span>
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="py-1 text-black font-bold">
                                                                        TRUCKER
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.truckerName || "-"}
                                                                        </span>
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td className="py-1 text-black font-bold">
                                                                        TRUCKER DETAILS
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.truckerAddress || "-"}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* RIGHT SIDE */}
                                                    <div className="px-2 py-0.5 border-l border-gray-400">
                                                        <table className="w-full text-xs">
                                                            <tbody className='text-[10px]'>
                                                                <tr>
                                                                    <td className="py-1 text-black font-bold w-32">
                                                                        HAZARDOUS
                                                                    </td>
                                                                    <td className="py-1 text-gray-900">
                                                                        :{" "}
                                                                        <span className="font-semibold">
                                                                            {formData?.hazCargo ? "Yes" : "No"}
                                                                        </span>
                                                                    </td>
                                                                </tr>

                                                                {formData?.hazCargo && (
                                                                    <>
                                                                        <tr>
                                                                            <td className="py-1 text-black font-bold">
                                                                                IMO CLASS
                                                                            </td>
                                                                            <td className="py-1 text-gray-900">
                                                                                :{" "}
                                                                                <span className="font-semibold">
                                                                                    {formData?.imoClass || "-"}
                                                                                </span>
                                                                            </td>
                                                                        </tr>

                                                                        <tr>
                                                                            <td className="py-1 text-black font-bold">
                                                                                PKG #
                                                                            </td>
                                                                            <td className="py-1 text-gray-900">
                                                                                :{" "}
                                                                                <span className="font-semibold">
                                                                                    {formData?.package || "-"}
                                                                                </span>
                                                                            </td>
                                                                        </tr>

                                                                        <tr>
                                                                            <td className="py-1 text-black font-bold">
                                                                                UN NBR
                                                                            </td>
                                                                            <td className="py-1 text-gray-900">
                                                                                :{" "}
                                                                                <span className="font-semibold">
                                                                                    {formData?.unnbr || "-"}
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    </>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {
                                            formData?.CarrierDetails?.map((carrier, index) => (
                                                <React.Fragment key={`rate-quote-carrier-${index}`}>
                                                    {/* Carrier band — light gray, 3-column row separated by vertical lines */}
                                                    <div className="rate-quote-print-carrier-vessel-block mb-2 border border-gray-400 bg-white">
                                                        <div
                                                            className="rate-quote-print-carrier-row grid grid-cols-3"
                                                            style={{ backgroundColor: SECTION_HEADER_BG }}
                                                        >
                                                            <div className="px-2 py-1">
                                                                <p className="m-0 text-[11px] font-bold uppercase tracking-wide text-black">
                                                                    CARRIER: <span className="font-bold normal-case">{carrier?.carrierName}</span>
                                                                </p>
                                                            </div>
                                                            <div className="px-2 py-1">
                                                                <p className="m-0 text-[11px] font-bold uppercase tracking-wide text-black">
                                                                    POL: <span className="font-bold normal-case">{formData?.pol} [{formData?.portOfLoad}]</span>
                                                                </p>
                                                            </div>
                                                            <div className="px-2 py-1">
                                                                <p className="m-0 text-[11px] font-bold uppercase tracking-wide text-black">
                                                                    POD: <span className="font-bold normal-case">{formData?.pod} [{formData?.portOfDischarge}]</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Vessel Table — clean underlined headers (no gray band) */}
                                                    <div className="rate-quote-print-vessel-body mb-3 px-1">
                                                        <table className="rate-quote-print-vessel-table w-full border-collapse text-[10px]">
                                                            <thead>
                                                                <tr style={{ borderBottom: '1px solid #000' }}>
                                                                    <th className="py-1 pl-2 text-left text-[10px] font-bold uppercase tracking-wide text-black">
                                                                        Vessel
                                                                    </th>
                                                                    <th className="py-1 text-left text-[10px] font-bold uppercase tracking-wide text-black">
                                                                        Voyage
                                                                    </th>
                                                                    <th className="py-1 text-center text-[10px] font-bold uppercase tracking-wide text-black">
                                                                        C/O
                                                                    </th>
                                                                    <th className="py-1 text-center text-[10px] font-bold uppercase tracking-wide text-black">
                                                                        ETD
                                                                    </th>
                                                                    <th className="py-1 text-center text-[10px] font-bold uppercase tracking-wide text-black">
                                                                        ETA
                                                                    </th>
                                                                    <th className="py-1 pr-2 text-center text-[10px] font-bold uppercase tracking-wide text-black">
                                                                        Transit Time
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {!carrier?.ratequoteVessel?.length || carrier?.ratequoteVessel?.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan={6} className="py-1 text-center text-black">
                                                                            &nbsp;
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    carrier?.ratequoteVessel?.map(
                                                                        (v, i) =>
                                                                            v?.isDeleted !== 1 && (
                                                                                <tr key={i}>
                                                                                    <td className="py-0.5 pl-2 font-semibold text-black">
                                                                                        {v?.vesselName || "-"}
                                                                                    </td>
                                                                                    <td className="py-0.5 text-black">{v?.voyage || "-"}</td>
                                                                                    <td className="py-0.5 text-center text-black">
                                                                                        {v?.cargoCutoff
                                                                                            ? moment(v?.cargoCutoff).format("DD MMM YYYY")
                                                                                            : "-"}
                                                                                    </td>
                                                                                    <td className="py-0.5 text-center text-black">
                                                                                        {v?.etd ? moment(v?.etd).format("DD MMM YYYY") : "-"}
                                                                                    </td>
                                                                                    <td className="py-0.5 text-center text-black">
                                                                                        {v?.eta ? moment(v?.eta).format("DD MMM YYYY") : "-"}
                                                                                    </td>
                                                                                    <td className="py-0.5 pr-2 text-center font-medium text-black">
                                                                                        {v?.transitTime || "-"}
                                                                                    </td>
                                                                                </tr>
                                                                            )
                                                                    )
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* Charges Table — clean underlined headers, totals row with top line, no gray band */}
                                                    <div className="rate-quote-print-charges-wrap mb-3 px-1">
                                                        <table className="rate-quote-print-charges w-full text-[10px] border-collapse">
                                                            <thead>
                                                                <tr style={{ borderBottom: '1px solid #000' }}>
                                                                    <th className="py-1 pl-2 text-left text-[10px] font-bold text-black">Charge Description</th>
                                                                    <th className="py-1 text-center text-[10px] font-bold text-black">Currency</th>
                                                                    <th className="py-1 text-center text-[10px] font-bold text-black">Basis</th>
                                                                    {carrier?.rateQuoteContainer?.map((charge, idx) => (
                                                                        <th key={idx} className="py-1 pr-2 text-right text-[10px] font-bold uppercase tracking-wide text-black">
                                                                            {charge?.numberOfContainer + ' X ' + charge?.contTypeName}
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {carrier?.rateCharges?.map((charge, index) => (
                                                                    <tr key={index}>
                                                                        <td className="py-0.5 pl-2 font-semibold text-black uppercase">{charge?.chargedescription}</td>
                                                                        <td className="py-0.5 text-center text-black">{charge?.currency || currency}</td>
                                                                        <td className="py-0.5 text-center text-black">{charge?.basis || 'per cntr'}</td>
                                                                        {
                                                                            carrier?.rateQuoteContainer?.map((c, i) => (
                                                                                <td key={i} className="py-0.5 pr-2 text-right font-medium text-black">
                                                                                    {(charge?.['set' + (i + 1) + 'sellrate'])?.toFixed(2)}
                                                                                </td>
                                                                            ))
                                                                        }
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr>
                                                                    <td className="py-1"></td>
                                                                    <td className="py-1"></td>
                                                                    <td className="py-1"></td>
                                                                    {
                                                                        carrier?.rateQuoteContainer?.map((c, i) => {
                                                                            let grandTotal = 0;
                                                                            carrier?.rateCharges?.map((charge) => {
                                                                                grandTotal += (charge?.['set' + (i + 1) + 'sellrate'] || 0)
                                                                            })
                                                                            return (
                                                                                <td
                                                                                    key={i}
                                                                                    className="py-1 pr-2 text-right font-bold text-black text-[11px]"
                                                                                    style={{ borderTop: '1px solid #000' }}
                                                                                >
                                                                                    ${(grandTotal).toFixed(2)}
                                                                                </td>
                                                                            )
                                                                        })
                                                                    }
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                    </div>
                                                </React.Fragment>
                                            ))
                                        }

                                        {/* Cargo Details */}
                                        {
                                            formData?.shippingTerm != 'FCL' && (
                                                <div className="grid grid-cols-1 gap-0 mb-4 border border-gray-400">
                                                    <div
                                                        className="rate-quote-print-section-head px-3 py-1.5 text-left"
                                                        style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}
                                                    >
                                                        <h3 className="m-0 font-bold text-[11px] uppercase tracking-wide text-black">
                                                            Cargo Details
                                                        </h3>
                                                    </div>
                                                    <div className="grid">
                                                        <div className="p-0 border-l ">
                                                            {/* Vessel Table */}
                                                            <div className="">
                                                                <table className="w-full text-[10px]">
                                                                    <thead>
                                                                        <tr className="border-b border-gray-400 bg-gray-100">
                                                                            <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
                                                                                Pc
                                                                            </th>
                                                                            <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
                                                                                Pkg
                                                                            </th>
                                                                            <th rowSpan="2" className="px-2 py-2 text-left text-gray-900 w-[220px]">
                                                                                Marks & Numbers
                                                                            </th>
                                                                            <th colSpan="4" className="px-2 py-2 text-center text-gray-900">
                                                                                Dimensions
                                                                            </th>
                                                                            <th colSpan="2" className="px-2 py-2 text-center text-gray-900">
                                                                                Weight
                                                                            </th>
                                                                            <th colSpan="2" className="px-2 py-2 text-center text-gray-900">
                                                                                Measurement
                                                                            </th>
                                                                            <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
                                                                                Cntr
                                                                            </th>
                                                                            <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
                                                                                HS
                                                                            </th>
                                                                        </tr>
                                                                        <tr className="border-b border-gray-300 text-[10px]">
                                                                            <th className="px-1 py-1 text-center text-gray-900">L</th>
                                                                            <th className="px-1 py-1 text-center text-gray-900">W</th>
                                                                            <th className="px-1 py-1 text-center text-gray-900">H</th>
                                                                            <th className="px-1 py-1 text-center text-gray-900">Type</th>
                                                                            <th className="px-1 py-1 text-center text-gray-900">KG</th>
                                                                            <th className="px-1 py-1 text-center text-gray-900">LB</th>
                                                                            <th className="px-1 py-1 text-center text-gray-900">CFT</th>
                                                                            <th className="px-1 py-1 text-center text-gray-900">CBM</th>
                                                                        </tr>
                                                                    </thead>

                                                                    <tbody>
                                                                        {
                                                                            !formData?.cargoUnits?.length || formData?.cargoUnits?.length === 0 ? (
                                                                                <tr>
                                                                                    <td colSpan="13" className="py-2 text-center text-gray-700">
                                                                                        No cargo data available
                                                                                    </td>
                                                                                </tr>
                                                                            )
                                                                                :

                                                                                formData?.cargoUnits?.map(
                                                                                    (row, index) =>
                                                                                        !row?.isdeleted && (
                                                                                            <tr
                                                                                                key={index}
                                                                                                className="border-b border-gray-200"
                                                                                            >
                                                                                                <td className="px-2 py-1 text-center text-gray-700">
                                                                                                    {row?.pices_No || "-"}
                                                                                                </td>

                                                                                                <td className="px-2 py-1 text-gray-700">
                                                                                                    {row?.units_Type || "-"}
                                                                                                </td>

                                                                                                <td className="px-2 py-1 font-medium text-gray-700">
                                                                                                    {row?.description || "-"}
                                                                                                </td>

                                                                                                <td className="px-1 py-1 text-center text-gray-700">
                                                                                                    {row?.dimensions_L || "-"}
                                                                                                </td>
                                                                                                <td className="px-1 py-1 text-center text-gray-700">
                                                                                                    {row?.dimensions_W || "-"}
                                                                                                </td>
                                                                                                <td className="px-1 py-1 text-center text-gray-700">
                                                                                                    {row?.dimensions_H || "-"}
                                                                                                </td>
                                                                                                <td className="px-1 py-1 text-center text-gray-700">
                                                                                                    {row?.dimensions_Type || "-"}
                                                                                                </td>

                                                                                                <td className="px-1 py-1 text-right text-gray-700">
                                                                                                    {row?.weights_KG || "-"}
                                                                                                </td>
                                                                                                <td className="px-1 py-1 text-right text-gray-700">
                                                                                                    {row?.weights_LB || "-"}
                                                                                                </td>

                                                                                                <td className="px-1 py-1 text-right text-gray-700">
                                                                                                    {row?.measurment_CFT || "-"}
                                                                                                </td>
                                                                                                <td className="px-1 py-1 text-right text-gray-700">
                                                                                                    {row?.measurment_CBM || "-"}
                                                                                                </td>

                                                                                                <td className="px-2 py-1 text-gray-700">
                                                                                                    {row?.container_ForUnit || "-"}
                                                                                                </td>

                                                                                                <td className="px-2 py-1 text-gray-700">
                                                                                                    {row?.hsCode || "-"}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ),
                                                                                )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }

                                        {
                                            !clauses?.length || clauses?.length === 0 ?
                                                null :
                                                (
                                                    <div className="grid grid-cols-1 gap-0 mb-3 border border-gray-400">
                                                        <div
                                                            className="rate-quote-print-section-head px-3 py-1.5 text-left"
                                                            style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}
                                                        >
                                                            <h3 className="m-0 font-bold text-[11px] uppercase tracking-wide text-black">
                                                                Clause
                                                            </h3>
                                                        </div>
                                                        <div className="grid">
                                                            <div className=" border-l ">
                                                                {/* Vessel Table */}
                                                                <div className="mb-3">
                                                                    <table className="w-full text-[10px]">
                                                                        <thead>
                                                                            <tr className="border-b border-gray-300">
                                                                                <th className="py-1 px-1 text-left font-medium text-gray-700 whitespace-nowrap">
                                                                                    CLAUSE TYPE
                                                                                </th>
                                                                                <th className="py-1 px-1 text-left font-medium text-gray-700 whitespace-nowrap">
                                                                                    MODULE TYPE
                                                                                </th>
                                                                                <th className="py-1 px-1 text-center font-medium text-gray-700 whitespace-nowrap">
                                                                                    CLAUSE CATEGORY
                                                                                </th>
                                                                                <th className="py-1 px-1 text-center font-medium text-gray-700">
                                                                                    DESCRIPTION
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {
                                                                                !clauses?.length || clauses?.length === 0 ? (
                                                                                    <tr>
                                                                                        <td colSpan="6" className="py-1 text-center">
                                                                                            No clause data available
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                                    :
                                                                                    clauses?.map(
                                                                                        (clause, i) =>
                                                                                        (
                                                                                            <tr key={i} className="border-b border-gray-200">
                                                                                                <td className="py-1 font-semibold text-gray-900 px-1">
                                                                                                    {clause?.ClauseType}
                                                                                                </td>

                                                                                                <td className="py-1 text-gray-700 px-1">
                                                                                                    {clause?.ModuleType}
                                                                                                </td>

                                                                                                <td className="py-1 text-center text-gray-700 px-1">
                                                                                                    {clause?.ClausesCategoryType}
                                                                                                </td>

                                                                                                <td className="py-1  text-gray-700 px-1">
                                                                                                    {clause?.ClauseDescription}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ),
                                                                                    )}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                        }



                                        {/* Customer Remarks */}
                                        <div className="mb-3">
                                            <h3 className="font-bold text-[10px] text-gray-900 uppercase mb-1">Customer Remarks:</h3>
                                            <p className="text-[10px] text-gray-700 uppercase whitespace-pre-line leading-relaxed">
                                                {formData?.customerRemarks || `BASED ON GENERAL CARGO.\nOCEANFREIGHT IS INCLUSIVE OF BUNKER, ISPS, TERMINAL, PORT, LOW SULFUR AND ENERGY SURCHARGE.`}
                                            </p>
                                            <h3 className="font-bold text-[10px] text-gray-900 uppercase mb-1 mt-1">Country Clause:</h3>
                                            <p className="text-[10px] text-gray-700 uppercase whitespace-pre-line leading-relaxed">
                                                {formData?.countryClause || "-"}
                                            </p>
                                        </div>

                                        {/* Signature footer — kept right after the content */}
                                        <div className="border-t border-black pt-4 mt-6">
                                            <div className="flex justify-end">
                                                <div className="text-right" style={{ minWidth: '240px' }}>
                                                    <p className="text-[11px] font-bold text-black uppercase tracking-wide mb-1">Customer Acceptance:</p>
                                                    <div className="border-b border-black w-56 h-6 ml-auto"></div>
                                                    <p className="text-[10px] text-black font-semibold mt-0.5">Signature &amp; Date</p>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </td>
                            </tr>
                        </tbody>

                    </table>
                    {/* END print table wrapper */}

                    {/* Powered-by — sibling of <table>, anchored to bottom of A4 page (screen) /
                        flows once at end of document (print). Placed OUTSIDE the table on
                        purpose, so table/td fragmentation never repeats or splits it. */}
                    <div className="rate-quote-powered-by">
                        Powered by {MasterCompanyDetails?.[0]?.PoweredBy}
                    </div>

                </div>
            </div>

            {
                loader ?
                    <div className="min-h-screen w-screen absolute top-0 left-0 flex items-center justify-center bg-[rgba(0,0,0,0.6)]">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#0072FF]"></div>
                    </div>
                    : null
            }

            {/* Print Styles */}
            <style>
                {`
.print-header-spacer {
    height: 0;
}

/* Classic document body — monospace, dense, document-style */
.a4-page .print-content,
.a4-page .print-content table,
.a4-page .print-content td,
.a4-page .print-content th,
.a4-page .print-content p,
.a4-page .print-content span {
    font-family: 'Courier New', Courier, 'Lucida Console', monospace;
}

/* Print hierarchy:
   - Labels (text-black font-bold) → pure black, heavy weight, very clear on print
   - Values (text-gray-900 / text-gray-700 / spans) → medium gray, lighter than labels */
.a4-page .print-content .text-black {
    color: #000 !important;
}

.a4-page .print-content .font-bold {
    font-weight: 800 !important;
    letter-spacing: 0.01em;
}

.a4-page .print-content .text-gray-900 {
    color: #1f2937 !important;
    font-weight: 600 !important;
}

.a4-page .print-content .text-gray-700 {
    color: #374151 !important;
    font-weight: 600 !important;
}

/* Title above the document */
.rate-quote-print-doc-title {
    font-family: Arial, Helvetica, sans-serif !important;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #000;
}

/* Section bands — classic light gray with black text */
.rate-quote-print-section-head {
    background-color: #d9d9d9 !important;
    color: #000 !important;
    text-align: left;
    border-bottom: 1px solid #000;
}

.rate-quote-print-section-head h3 {
    color: #000 !important;
}

/* Strong, classic black borders on document boxes */
.print-content .border-gray-400 {
    border-color: #000 !important;
}

.print-content .border-gray-300 {
    border-color: #000 !important;
}

.print-content .border-gray-200 {
    border-color: #000 !important;
}

/* Internal vertical dividers should also be black */
.rate-quote-print-carrier-row > div + div {
    border-left: 1px solid #000;
}

/* Carrier row band — same light gray as the section heads (classic doc) */
.rate-quote-print-carrier-row {
    background-color: #d9d9d9 !important;
    border-bottom: 1px solid #000 !important;
}

/* Charges & vessel table headers — NO background, just bold black with bottom rule (matches image) */
.rate-quote-print-charges thead tr,
.rate-quote-print-vessel-table thead tr {
    background-color: transparent !important;
    background: transparent !important;
    border-bottom: 1px solid #000 !important;
}

.rate-quote-print-charges thead th,
.rate-quote-print-vessel-table thead th {
    color: #000 !important;
    font-weight: 700 !important;
    background-color: transparent !important;
    background: transparent !important;
    border-bottom: 1px solid #000 !important;
}

.rate-quote-print-charges tbody tr,
.rate-quote-print-vessel-table tbody tr {
    background-color: transparent !important;
    background: transparent !important;
    border: none !important;
}

.rate-quote-print-charges tbody td,
.rate-quote-print-vessel-table tbody td {
    color: #000 !important;
    border: none !important;
}

/* Total row top rule — only the cells with totals get the line */
.rate-quote-print-charges tfoot td {
    border: none !important;
}

/* Cargo Details table headers */
.print-content table thead th {
    color: #000 !important;
}

/* Footer total separator line */
.rate-quote-print-charges tfoot td {
    border-top: 1px solid #000 !important;
}

/* ── Powered-by footer ────────────────────────────────────────────────
   The .rate-quote-powered-by element is a direct sibling of <table>
   inside .a4-page (NOT inside any <td>) so it never participates in
   table-cell fragmentation, which is what previously caused the element
   to repeat or land on a blank second page.

   • On screen we turn .a4-page into a flex column so the element sits at
     the bottom-left of the visual A4 sheet.
   • In print we let it flow naturally — it ends up as the last block in
     the document, appearing once at the end of the last printed page.
   ──────────────────────────────────────────────────────────────────── */
.a4-page .rate-quote-powered-by {
    margin-top: 12px;
    text-align: left;
    padding: 6px 12mm 4px 12mm;          /* match the table inner padding */
    border-top: 1px solid #e5e7eb;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 9px;
    line-height: 1.35;
    color: #6b7280;
    font-weight: 600;
    background: #fff;
}

.a4-page .rate-quote-powered-by,
.a4-page .rate-quote-powered-by * {
    font-family: Arial, Helvetica, sans-serif !important;
}

@media screen {
    /* Make .a4-page a flex column so the powered-by can use margin-top:auto
       to pin itself to the bottom of the visual A4 sheet. */
    .a4-page {
        display: flex;
        flex-direction: column;
    }
    .a4-page > table {
        flex: 0 0 auto;
    }
    .a4-page .rate-quote-powered-by {
        margin-top: auto;
    }
}

@media print {

    /* Match on-screen preview: honor backgrounds / borders in print dialog */
    .a4-page,
    .a4-page * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    .print-header-spacer {
        height: 6px;
    }

    /* Global print img rule uses max-width:100%; keep header logo visible */
    img.rate-quote-print-header-logo {
        max-width: 132px !important;
        max-height: 48px !important;
        width: auto !important;
        height: auto !important;
        object-fit: contain !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    .rate-quote-print-doc-title {
        color: #000 !important;
        font-family: Arial, Helvetica, sans-serif !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    .rate-quote-print-doc-header {
        border-bottom: 1px solid #000 !important;
    }

    /* High-specificity overrides — beat any cached global rules from index.css */
    html body .a4-page .rate-quote-print-section-head,
    html body div.a4-page div.rate-quote-print-section-head {
        background: #d9d9d9 !important;
        background-color: #d9d9d9 !important;
        color: #000 !important;
        border-bottom: 1px solid #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    html body .a4-page .rate-quote-print-section-head h3,
    html body div.a4-page div.rate-quote-print-section-head h3 {
        color: #000 !important;
        background: transparent !important;
        background-color: transparent !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    html body .a4-page .rate-quote-print-doc-title {
        color: #000 !important;
        background-color: transparent !important;
        font-family: Arial, Helvetica, sans-serif !important;
    }

    html body .a4-page .rate-quote-print-carrier-row {
        background-color: #d9d9d9 !important;
        background: #d9d9d9 !important;
        color: #000 !important;
        border-bottom: 1px solid #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    /* Force monospace font in print */
    .a4-page .print-content,
    .a4-page .print-content * {
        font-family: 'Courier New', Courier, 'Lucida Console', monospace !important;
    }

    .a4-page .print-content h1,
    .a4-page .rate-quote-print-doc-title {
        font-family: Arial, Helvetica, sans-serif !important;
    }

    /* Print hierarchy — labels bold/clear, values lighter for visual separation */
    html body .a4-page .print-content .text-black,
    html body .a4-page .print-content .font-bold {
        color: #000 !important;
    }

    html body .a4-page .print-content .font-bold {
        font-weight: 800 !important;
        letter-spacing: 0.01em !important;
    }

    html body .a4-page .print-content .text-gray-900 {
        color: #1f2937 !important;
        font-weight: 600 !important;
    }

    html body .a4-page .print-content .text-gray-700 {
        color: #374151 !important;
        font-weight: 600 !important;
    }

    /* Charges & Vessel tables — clean rows (no row dividers), header underline only */
    .rate-quote-print-charges-wrap th,
    .rate-quote-print-charges-wrap td,
    .rate-quote-print-vessel-body th,
    .rate-quote-print-vessel-body td {
        border: none !important;
        padding: 3px 6px !important;
        vertical-align: middle !important;
        background-color: transparent !important;
        background: transparent !important;
    }

    .rate-quote-print-charges-wrap thead tr,
    .rate-quote-print-vessel-body thead tr {
        border-bottom: 1px solid #000 !important;
        background: transparent !important;
        background-color: transparent !important;
    }

    .rate-quote-print-charges-wrap thead th,
    .rate-quote-print-vessel-body thead th {
        border-bottom: 1px solid #000 !important;
        background-color: transparent !important;
        background: transparent !important;
        color: #000 !important;
        font-weight: 700 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    /* Total row: line above the total cells only */
    .rate-quote-print-charges-wrap tfoot td {
        border-bottom: none !important;
        border-top: none !important;
    }

    .rate-quote-print-charges-wrap tfoot td[style*="border-top"] {
        border-top: 1px solid #000 !important;
        padding-top: 4px !important;
    }

    .rate-quote-print-carrier-row > div + div {
        border-left: 1px solid #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .rate-quote-print-carrier-row {
        background-color: #d9d9d9 !important;
        border-bottom: 1px solid #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    /* Tailwind borders often disappear in print — force black borders to match classic doc */
    .print-content .border-gray-400 {
        border-color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    thead .border-gray-300 {
        border-color: #000 !important;
        border-bottom-color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .print-content .border-gray-300 {
        border-color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .print-content .border-gray-200 {
        border-color: #000 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    .print-content .border-gray-400,
    .print-content .border-gray-300,
    .print-content .border-gray-200,
    .print-content .border-t,
    .print-content .border-b,
    .print-content .border-l,
    .print-content .border-r,
    .print-content .border {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    /* Tailwind gray band rows (carrier / table headers) — light gray classic doc band */
    .a4-page .bg-gray-100 {
        background-color: #f1f1f1 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    .a4-page .bg-gray-50 {
        background-color: #f1f1f1 !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    @page {
        size: A4;
        margin: 6mm;
    }

    html,
    body {
        width: 100% !important;
        max-width: 100% !important;
        height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        print-color-adjust: exact !important;
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
    }

    body * {
        box-sizing: border-box;
    }

    /* Hide action buttons */
    .print\\:hidden {
        display: none !important;
    }

    /* Keep box chrome only — width comes from inline style (210mm) like BookingPrint */
    .a4-page {
        margin: 0 auto !important;
        padding: 0 !important;
        box-shadow: none !important;
        background: #fff !important;
        /* The inline style forces minHeight:297mm for screen, but in print the
           @page box (size: A4, margin: 6mm) only gives us 285mm of usable
           height. A 297mm wrapper bleeds ~12mm onto a blank second page.
           Let the wrapper size to its content in print. */
        min-height: 0 !important;
        height: auto !important;
        /* Plain block flow in print — flex stays screen-only (see @media screen) */
        display: block !important;
    }

    table.rate-quote-print-shell {
        width: 100% !important;
        border-collapse: collapse !important;
        border-spacing: 0 !important;
    }

    table.rate-quote-print-shell thead {
        display: table-header-group;
    }

    table.rate-quote-print-shell tfoot {
        display: table-footer-group;
    }

    /* Only main document body rows — avoids breaking nested header table inside thead */
    table.rate-quote-print-shell > tbody > tr > td,
    table.rate-quote-print-shell > tbody > tr > th {
        margin: 0 !important;
        vertical-align: top !important;
        background: #fff !important;
    }

    table.rate-quote-print-shell > thead > tr > th,
    table.rate-quote-print-shell > thead > tr > td {
        background: #fff !important;
        background-color: #fff !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }

    table.rate-quote-print-header-inner td {
        background-color: #fff !important;
    }

    /* Prevent awkward page breaks */
    .section-block,
    .quote-table,
    .clause-table {
        page-break-inside: avoid;
        break-inside: avoid;
    }

    /* Natural block flow in print — no flex / min-height tricks */
    .print-content {
        page-break-inside: auto;
    }

    /* Powered-by (sibling of <table> inside .a4-page):
       Let it flow naturally as the very last DOM block in the document
       so it appears exactly once, at the end of the last printed page.
       Avoid starting a new page right before it. */
    .a4-page .rate-quote-powered-by {
        margin-top: 12px !important;
        text-align: left !important;
        font-family: Arial, Helvetica, sans-serif !important;
        page-break-before: avoid !important;
        break-before: avoid !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
    }

    .a4-page .rate-quote-powered-by,
    .a4-page .rate-quote-powered-by * {
        font-family: Arial, Helvetica, sans-serif !important;
    }

    /* Utility resets */
    .print\\:shadow-none {
        box-shadow: none !important;
    }

    .print\\:bg-white {
        background: #fff !important;
    }

    .print\\:py-0 {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }

    /* Outer shell only — nested charge/detail tables keep their own borders */
    table.rate-quote-print-shell,
    table.rate-quote-print-shell > thead,
    table.rate-quote-print-shell > tbody,
    table.rate-quote-print-shell > tfoot,
    table.rate-quote-print-shell > thead > tr,
    table.rate-quote-print-shell > tbody > tr,
    table.rate-quote-print-shell > tfoot > tr,
    table.rate-quote-print-shell > thead > tr > th,
    table.rate-quote-print-shell > thead > tr > td,
    table.rate-quote-print-shell > tbody > tr > td,
    table.rate-quote-print-shell > tfoot > tr > td {
        border: none !important;
    }

    /* Images (exclude header logo — it has its own max dimensions) */
    img:not(.rate-quote-print-header-logo) {
        max-width: 100% !important;
        page-break-inside: avoid;
    }

    /* Avoid layout issues */
    .grid,
    .flex {
        break-inside: auto;
        page-break-inside: auto;
    }
}
`}
            </style>
        </div >
    );
    // return (
    //     <div className="min-h-screen bg-gray-200 print:bg-white">
    //         {/* Action Bar - Hidden when printing */}
    //         <div className="print:hidden bg-white shadow-sm border-b border-gray-300 sticky top-0 z-10">
    //             <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-center">
    //                 {/* <button
    //                     onClick={() => navigate(-1)}
    //                     className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all font-medium"
    //                 >
    //                     <ArrowLeft className="w-4 h-4" />
    //                     Back to Rate Quotes
    //                 </button> */}
    //                 <div className="flex items-center gap-3">
    //                     <button
    //                         onClick={handleDownload}
    //                         className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-medium border border-gray-300"
    //                     >
    //                         <Download className="w-4 h-4" />
    //                         Download PDF
    //                     </button>
    //                     <button
    //                         onClick={handlePrint}
    //                         className="flex items-center gap-2 px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg transition-all font-medium"
    //                     >
    //                         <Printer className="w-4 h-4" />
    //                         Print
    //                     </button>
    //                 </div>
    //             </div>
    //         </div>

    //         {/* A4 Print Container */}
    //         <div className="flex justify-center py-6 print:py-0">
    //             <div className="a4-page bg-white shadow-xl print:shadow-none">
    //                 {/* Logo Header */}
    //                 <div className="px-6 pt-6 pb-4 flex justify-between items-start border-b border-gray-200">
    //                     <div className="flex items-center gap-3">

    //                         <div>
    //                             <img
    //                                 src={MasterCompanyDetails?.[0]?.CompanyLogo || "/shipez-logo.png"}
    //                                 alt={MasterCompanyDetails?.[0]?.CompanyName || "Company Logo"}
    //                                 className="h-12 w-auto"
    //                             />
    //                             <p className="text-xs text-gray-500">
    //                                 Intelligent Freight Solutions
    //                             </p>
    //                         </div>

    //                     </div>
    //                     <div className="text-right text-xs text-gray-600">
    //                         {MasterCompanyDetails?.[0]?.ProfileAddressPrint
    //                             ?.split(',')
    //                             .map((line, index) => (
    //                                 <p key={index}>{line.trim()}</p>
    //                             ))}



    //                         {/* <p>1074 BROADWAY, SUITE # 200,WEST LONG BRANCH NJ 07764,</p>
    //                         <p>TEL (732) 283-5300 </p>
    //                         <p>FAX: (732) 283-2423</p> */}
    //                     </div>
    //                 </div>
    //                 {/* Content */}
    //                 <div className="p-6">
    //                     {/* Customer & Quote Details Row */}
    //                     <div className="grid grid-cols-1 gap-0 mb-3 border border-gray-400">
    //                         <div className="rate-quote-print-section-head px-3 py-1" style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}>
    //                             <h3 className="font-bold text-xs uppercase tracking-wide">Customer</h3>
    //                         </div>
    //                         <div className="grid grid-cols-2 gap-0">
    //                             <div className="px-2 py-0.5 text-xs">
    //                                 <p className="font-semibold text-gray-900">{formData?.customerName}</p>
    //                                 <p className="text-gray-700 whitespace-pre-line text-xs mt-1">{formData?.customerAddress}</p>
    //                                 <p className="text-gray-700 text-xs">TEL: {formData?.customerTel}</p>
    //                                 <p className="text-gray-700 text-xs">{formData?.customerEmail}</p>
    //                             </div>
    //                             <div className="px-2 py-0.5 border-l border-gray-400 ">
    //                                 <table className="w-full text-[11px]">
    //                                     <tbody>
    //                                         <tr>
    //                                             <td className="py-0.5 text-black font-bold">RQ NUMBER.</td>
    //                                             <td className="py-0.5 text-gray-900">: {formData?.rateQuoteNumber}</td>
    //                                         </tr>
    //                                         <tr>
    //                                             <td className="py-0.5 text-black font-bold">QUOTE DATE</td>
    //                                             <td className="py-0.5 text-gray-900">: {formData?.createdOn ? moment(formData?.createdOn).format('DD MMM YYYY') : ''}</td>
    //                                         </tr>
    //                                         <tr>
    //                                             <td className="py-0.5 text-black font-bold">EFFECTIVE DATE</td>
    //                                             <td className="py-0.5 text-gray-900">: {formData?.effectiveDate ? moment(formData?.effectiveDate).format('DD MMM YYYY') : ''}</td>
    //                                         </tr>
    //                                         <tr>
    //                                             <td className="py-0.5 text-black font-bold">EXPIRY DATE</td>
    //                                             <td className="py-0.5 text-gray-900">: {formData?.valideDate ? moment(formData?.valideDate).format('DD MMM YYYY') : ''}</td>
    //                                         </tr>
    //                                         <tr>
    //                                             <td className="py-0.5 text-black font-bold">QUOTED BY</td>
    //                                             <td className="py-0.5 text-gray-900">: {formData?.quotedBy}</td>
    //                                         </tr>
    //                                         <tr>
    //                                             <td className="py-0.5 text-black font-bold">SALES PERSON</td>
    //                                             <td className="py-0.5 text-gray-900">: {SalesPersonName(formData?.salesPersonSno)}</td>
    //                                         </tr>
    //                                     </tbody>
    //                                 </table>
    //                             </div>
    //                         </div>
    //                     </div>


    //                     {/* Routing Instruction */}
    //                     <div className="mb-3 border border-gray-400">
    //                         <div className="grid grid-cols-1">
    //                             <div className="rate-quote-print-section-head px-3 py-1" style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}>
    //                                 <h3 className="font-bold text-xs uppercase tracking-wide text-white">
    //                                     Routing Instruction
    //                                 </h3>
    //                             </div>
    //                             <div className="grid grid-cols-2">
    //                                 <div className="px-2 py-0.5">
    //                                     <table className="w-full text-xs">
    //                                         <tbody className='text-[11px]'>
    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold w-28">
    //                                                     MOVE TYPE
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.moveType}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>
    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold w-28">
    //                                                     PLACE OF RECEIPT
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.origin}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>
    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold w-28">
    //                                                     PORT OF LOADING
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.portOfLoad}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>

    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold">
    //                                                     TRANSIT TIME
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.transitTime
    //                                                             ? `${formData?.transitTime} days`
    //                                                             : "-"}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>

    //                                         </tbody>
    //                                     </table>
    //                                 </div>
    //                                 <div className="px-2 py-0.5 border-l border-gray-400">
    //                                     <table className="w-full text-[11px]">
    //                                         <tbody>

    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold">
    //                                                     PORT OF DISCHARGE

    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.portOfDischarge ?? "-"
    //                                                         }
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>
    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold">
    //                                                     PLACE OF DELIVERY
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.destination ?? "-"
    //                                                         }
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>
    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold">
    //                                                     LOADING LOCATION
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.loadingLoc ?? "-"
    //                                                         }
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>

    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold">
    //                                                     COMMODITY
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.commodity}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>


    //                                         </tbody>
    //                                     </table>
    //                                 </div>
    //                             </div>
    //                         </div>
    //                     </div>

    //                     <div className="mb-3 border border-gray-400">
    //                         <div className="grid grid-cols-1">
    //                             {/* Header */}
    //                             <div className="rate-quote-print-section-head px-3 py-1" style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}>
    //                                 <h3 className="font-bold text-xs uppercase tracking-wide text-white">
    //                                     Container & Commodity
    //                                 </h3>
    //                             </div>
    //                             <div className="grid grid-cols-2">
    //                                 {/* LEFT SIDE */}
    //                                 <div className="px-2 py-0.5">
    //                                     <table className="w-full text-[11px]">
    //                                         <tbody>
    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold w-32">
    //                                                     COMMODITY
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.commodity || "-"}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>

    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold">
    //                                                     TRUCKER
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.truckerName || "-"}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>

    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold">
    //                                                     TRUCKER DETAILS
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.truckerAddress || "-"}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>
    //                                         </tbody>
    //                                     </table>
    //                                 </div>

    //                                 {/* RIGHT SIDE */}
    //                                 <div className="px-2 py-0.5 border-l border-gray-400">
    //                                     <table className="w-full text-xs">
    //                                         <tbody className='text-[11px]'>
    //                                             <tr>
    //                                                 <td className="py-1 text-black font-bold w-32">
    //                                                     HAZARDOUS
    //                                                 </td>
    //                                                 <td className="py-1 text-gray-900">
    //                                                     :{" "}
    //                                                     <span className="font-semibold">
    //                                                         {formData?.hazCargo ? "Yes" : "No"}
    //                                                     </span>
    //                                                 </td>
    //                                             </tr>

    //                                             {formData?.hazCargo && (
    //                                                 <>
    //                                                     <tr>
    //                                                         <td className="py-1 text-black font-bold">
    //                                                             IMO CLASS
    //                                                         </td>
    //                                                         <td className="py-1 text-gray-900">
    //                                                             :{" "}
    //                                                             <span className="font-semibold">
    //                                                                 {formData?.imoClass || "-"}
    //                                                             </span>
    //                                                         </td>
    //                                                     </tr>

    //                                                     <tr>
    //                                                         <td className="py-1 text-black font-bold">
    //                                                             PKG #
    //                                                         </td>
    //                                                         <td className="py-1 text-gray-900">
    //                                                             :{" "}
    //                                                             <span className="font-semibold">
    //                                                                 {formData?.package || "-"}
    //                                                             </span>
    //                                                         </td>
    //                                                     </tr>

    //                                                     <tr>
    //                                                         <td className="py-1 text-black font-bold">
    //                                                             UN NBR
    //                                                         </td>
    //                                                         <td className="py-1 text-gray-900">
    //                                                             :{" "}
    //                                                             <span className="font-semibold">
    //                                                                 {formData?.unnbr || "-"}
    //                                                             </span>
    //                                                         </td>
    //                                                     </tr>
    //                                                 </>
    //                                             )}
    //                                         </tbody>
    //                                     </table>
    //                                 </div>
    //                             </div>
    //                         </div>
    //                     </div>

    //                     {
    //                         formData?.CarrierDetails?.map((carrier, index) => (
    //                             <>
    //                                 {/* Carrier & Ports */}
    //                                 <div className="mb-3 border border-gray-400" >
    //                                     <div className="grid grid-cols-3 bg-gray-100">
    //                                         <div className="p-2 border-r border-gray-400">
    //                                             <p className="text-xs font-semibold text-gray-900">CARRIER: {carrier?.carrierName}</p>
    //                                         </div>
    //                                         <div className="p-2 border-r border-gray-400">
    //                                             <p className="text-xs font-semibold text-gray-900">POL: {formData?.pol} [{formData?.portOfLoad}]</p>
    //                                         </div>
    //                                         <div className="p-2">
    //                                             <p className="text-xs font-semibold text-gray-900">POD: {formData?.pod} [{formData?.portOfDischarge}]</p>
    //                                         </div>
    //                                     </div>
    //                                 </div>

    //                                 {/* Charges Table */}
    //                                 <div className="mb-3">
    //                                     <table className="w-full text-[11px]">
    //                                         <thead>
    //                                             <tr className="border-b border-gray-300">
    //                                                 <th className="py-1 text-left font-medium text-gray-700">Charge Description</th>
    //                                                 <th className="py-1 text-center font-medium text-gray-700">Currency</th>
    //                                                 <th className="py-1 text-center font-medium text-gray-700">Basis</th>
    //                                                 {carrier?.rateQuoteContainer?.map((charge, index) => (
    //                                                     <th className="py-1 text-right font-medium text-gray-700">{charge?.numberOfContainer + ' X ' + charge?.contTypeName}</th>
    //                                                 ))
    //                                                 }
    //                                             </tr>
    //                                         </thead>
    //                                         <tbody>
    //                                             {carrier?.rateCharges?.map((charge, index) => (
    //                                                 <tr key={index} className="border-b border-gray-200">
    //                                                     <td className="py-1 font-semibold text-gray-900 uppercase">{charge?.chargedescription}</td>
    //                                                     <td className="py-1 text-center text-gray-700">{charge?.currency || currency}</td>
    //                                                     <td className="py-1 text-center text-gray-700">{charge?.basis || 'per cntr'}</td>
    //                                                     {
    //                                                         carrier?.rateQuoteContainer?.map((c, i) => (
    //                                                             <td className="py-1 text-right font-medium text-gray-900">
    //                                                                 ${(charge?.['set' + (i + 1) + 'sellrate']).toFixed(2)}
    //                                                             </td>
    //                                                         ))
    //                                                     }
    //                                                 </tr>
    //                                             ))}
    //                                         </tbody>
    //                                         <tfoot>
    //                                             <tr>
    //                                                 <td className="py-1"></td>
    //                                                 <td className="py-1"></td>
    //                                                 <td className="py-1 text-center font-bold text-gray-900">Grand Total</td>
    //                                                 {
    //                                                     carrier?.rateQuoteContainer?.map((c, i) => {
    //                                                         let grandTotal = 0;
    //                                                         carrier?.rateCharges?.map((charge, index) => {
    //                                                             grandTotal += (charge?.['set' + (i + 1) + 'sellrate'])
    //                                                         })
    //                                                         return (
    //                                                             <td className="py-2 text-right font-bold text-gray-900 text-sm">
    //                                                                 ${(grandTotal).toFixed(2)}
    //                                                             </td>
    //                                                         )
    //                                                     })
    //                                                 }
    //                                             </tr>
    //                                         </tfoot>
    //                                     </table>
    //                                 </div>

    //                                 {/* Vessel Table */}
    //                                 <div className="grid grid-cols-1 gap-0 mb-4 border border-gray-400">
    //                                     <div className="rate-quote-print-section-head px-3 py-1" style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}>
    //                                         <h3 className="font-bold text-xs uppercase tracking-wide text-white">
    //                                             Vessel
    //                                         </h3>
    //                                     </div>
    //                                     <div className="grid">
    //                                         <div className=" border-l ">
    //                                             {/* Vessel Table */}
    //                                             <div className="mb-3">
    //                                                 <table className="w-full text-[11px]">
    //                                                     <thead>
    //                                                         <tr className="border-b border-gray-300">
    //                                                             <th className="py-1 pl-2 text-left font-medium text-gray-700">
    //                                                                 Vessel
    //                                                             </th>
    //                                                             <th className="py-1 text-left font-medium text-gray-700">
    //                                                                 Voyage
    //                                                             </th>
    //                                                             <th className="py-1 text-center font-medium text-gray-700">
    //                                                                 C/O
    //                                                             </th>
    //                                                             <th className="py-1 text-center font-medium text-gray-700">
    //                                                                 ETD
    //                                                             </th>
    //                                                             <th className="py-1 text-center font-medium text-gray-700">
    //                                                                 ETA
    //                                                             </th>
    //                                                             <th className="py-1 text-center font-medium text-gray-700 pr-2">
    //                                                                 Transit Time
    //                                                             </th>
    //                                                         </tr>
    //                                                     </thead>

    //                                                     <tbody>
    //                                                         {
    //                                                             !carrier?.ratequoteVessel?.length || carrier?.ratequoteVessel?.length === 0 ? (
    //                                                                 <tr>
    //                                                                     <td colSpan="6" className="py-1 text-center text-gray-700">
    //                                                                         No vessel data available
    //                                                                     </td>
    //                                                                 </tr>
    //                                                             )
    //                                                                 :

    //                                                                 carrier?.ratequoteVessel?.map(
    //                                                                     (v, i) =>
    //                                                                         v?.isDeleted !== 1 && (
    //                                                                             <tr key={i} className="border-b border-gray-200">
    //                                                                                 <td className="py-1 font-semibold text-gray-900 pl-2">
    //                                                                                     {v?.vesselName || "-"}
    //                                                                                 </td>

    //                                                                                 <td className="py-1 text-gray-700">
    //                                                                                     {v?.voyage || "-"}
    //                                                                                 </td>

    //                                                                                 <td className="py-1 text-center text-gray-700">
    //                                                                                     {v?.cargoCutoff
    //                                                                                         ? moment(v?.cargoCutoff).format(
    //                                                                                             "DD MMM YYYY",
    //                                                                                         )
    //                                                                                         : "-"}
    //                                                                                 </td>

    //                                                                                 <td className="py-1 text-center text-gray-700">
    //                                                                                     {v?.etd
    //                                                                                         ? moment(v?.etd).format("DD MMM YYYY")
    //                                                                                         : "-"}
    //                                                                                 </td>

    //                                                                                 <td className="py-1 text-center text-gray-700">
    //                                                                                     {v?.eta
    //                                                                                         ? moment(v?.eta).format("DD MMM YYYY")
    //                                                                                         : "-"}
    //                                                                                 </td>

    //                                                                                 <td className="py-1 text-center font-medium text-gray-900 pr-2">
    //                                                                                     {v?.transitTime || "-"}
    //                                                                                 </td>
    //                                                                             </tr>
    //                                                                         ),
    //                                                                 )}
    //                                                     </tbody>
    //                                                 </table>
    //                                             </div>
    //                                         </div>
    //                                     </div>
    //                                 </div>
    //                             </>
    //                         ))
    //                     }

    //                     {/* Cargo Details */}
    //                     {
    //                         formData?.shippingTerm != 'FCL' && (
    //                             <div className="grid grid-cols-1 gap-0 mb-4 border border-gray-400">
    //                                 <div className="rate-quote-print-section-head px-3 py-1" style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}>
    //                                     <h3 className="font-bold text-xs uppercase tracking-wide text-white">
    //                                         Cargo Details
    //                                     </h3>
    //                                 </div>
    //                                 <div className="grid">
    //                                     <div className="p-0 border-l ">
    //                                         {/* Vessel Table */}
    //                                         <div className="">
    //                                             <table className="w-full text-[11px]">
    //                                                 <thead>
    //                                                     <tr className="border-b border-gray-400 bg-gray-100">
    //                                                         <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
    //                                                             Pc
    //                                                         </th>
    //                                                         <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
    //                                                             Pkg
    //                                                         </th>
    //                                                         <th rowSpan="2" className="px-2 py-2 text-left text-gray-900 w-[220px]">
    //                                                             Marks & Numbers
    //                                                         </th>
    //                                                         <th colSpan="4" className="px-2 py-2 text-center text-gray-900">
    //                                                             Dimensions
    //                                                         </th>
    //                                                         <th colSpan="2" className="px-2 py-2 text-center text-gray-900">
    //                                                             Weight
    //                                                         </th>
    //                                                         <th colSpan="2" className="px-2 py-2 text-center text-gray-900">
    //                                                             Measurement
    //                                                         </th>
    //                                                         <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
    //                                                             Cntr
    //                                                         </th>
    //                                                         <th rowSpan="2" className="px-2 py-2 text-left text-gray-900">
    //                                                             HS
    //                                                         </th>
    //                                                     </tr>
    //                                                     <tr className="border-b border-gray-300 text-[10px]">
    //                                                         <th className="px-1 py-1 text-center text-gray-900">L</th>
    //                                                         <th className="px-1 py-1 text-center text-gray-900">W</th>
    //                                                         <th className="px-1 py-1 text-center text-gray-900">H</th>
    //                                                         <th className="px-1 py-1 text-center text-gray-900">Type</th>
    //                                                         <th className="px-1 py-1 text-center text-gray-900">KG</th>
    //                                                         <th className="px-1 py-1 text-center text-gray-900">LB</th>
    //                                                         <th className="px-1 py-1 text-center text-gray-900">CFT</th>
    //                                                         <th className="px-1 py-1 text-center text-gray-900">CBM</th>
    //                                                     </tr>
    //                                                 </thead>

    //                                                 <tbody>
    //                                                     {
    //                                                         !formData?.cargoUnits?.length || formData?.cargoUnits?.length === 0 ? (
    //                                                             <tr>
    //                                                                 <td colSpan="13" className="py-2 text-center text-gray-700">
    //                                                                     No cargo data available
    //                                                                 </td>
    //                                                             </tr>
    //                                                         )
    //                                                             :

    //                                                             formData?.cargoUnits?.map(
    //                                                                 (row, index) =>
    //                                                                     !row?.isdeleted && (
    //                                                                         <tr
    //                                                                             key={index}
    //                                                                             className="border-b border-gray-200"
    //                                                                         >
    //                                                                             <td className="px-2 py-1 text-center text-gray-700">
    //                                                                                 {row?.pices_No || "-"}
    //                                                                             </td>

    //                                                                             <td className="px-2 py-1 text-gray-700">
    //                                                                                 {row?.units_Type || "-"}
    //                                                                             </td>

    //                                                                             <td className="px-2 py-1 font-medium text-gray-700">
    //                                                                                 {row?.description || "-"}
    //                                                                             </td>

    //                                                                             <td className="px-1 py-1 text-center text-gray-700">
    //                                                                                 {row?.dimensions_L || "-"}
    //                                                                             </td>
    //                                                                             <td className="px-1 py-1 text-center text-gray-700">
    //                                                                                 {row?.dimensions_W || "-"}
    //                                                                             </td>
    //                                                                             <td className="px-1 py-1 text-center text-gray-700">
    //                                                                                 {row?.dimensions_H || "-"}
    //                                                                             </td>
    //                                                                             <td className="px-1 py-1 text-center text-gray-700">
    //                                                                                 {row?.dimensions_Type || "-"}
    //                                                                             </td>

    //                                                                             <td className="px-1 py-1 text-right text-gray-700">
    //                                                                                 {row?.weights_KG || "-"}
    //                                                                             </td>
    //                                                                             <td className="px-1 py-1 text-right text-gray-700">
    //                                                                                 {row?.weights_LB || "-"}
    //                                                                             </td>

    //                                                                             <td className="px-1 py-1 text-right text-gray-700">
    //                                                                                 {row?.measurment_CFT || "-"}
    //                                                                             </td>
    //                                                                             <td className="px-1 py-1 text-right text-gray-700">
    //                                                                                 {row?.measurment_CBM || "-"}
    //                                                                             </td>

    //                                                                             <td className="px-2 py-1 text-gray-700">
    //                                                                                 {row?.container_ForUnit || "-"}
    //                                                                             </td>

    //                                                                             <td className="px-2 py-1 text-gray-700">
    //                                                                                 {row?.hsCode || "-"}
    //                                                                             </td>
    //                                                                         </tr>
    //                                                                     ),
    //                                                             )}
    //                                                 </tbody>
    //                                             </table>
    //                                         </div>
    //                                     </div>
    //                                 </div>
    //                             </div>
    //                         )
    //                     }


    //                     <div className="grid grid-cols-1 gap-0 mb-3 border border-gray-400">
    //                         <div className="rate-quote-print-section-head px-3 py-1" style={{ backgroundColor: SECTION_HEADER_BG, color: SECTION_HEADER_TEXT }}>
    //                             <h3 className="font-bold text-xs uppercase tracking-wide text-white">
    //                                 Clause
    //                             </h3>
    //                         </div>
    //                         <div className="grid">
    //                             <div className=" border-l ">
    //                                 {/* Vessel Table */}
    //                                 <div className="mb-3">
    //                                     <table className="w-full text-[11px]">
    //                                         <thead>
    //                                             <tr className="border-b border-gray-300">
    //                                                 <th className="py-1 px-1 text-left font-medium text-gray-700 whitespace-nowrap">
    //                                                     CLAUSE TYPE
    //                                                 </th>
    //                                                 <th className="py-1 px-1 text-left font-medium text-gray-700 whitespace-nowrap">
    //                                                     MODULE TYPE
    //                                                 </th>
    //                                                 <th className="py-1 px-1 text-center font-medium text-gray-700 whitespace-nowrap">
    //                                                     CLAUSE CATEGORY
    //                                                 </th>
    //                                                 <th className="py-1 px-1 text-center font-medium text-gray-700">
    //                                                     DESCRIPTION
    //                                                 </th>
    //                                             </tr>
    //                                         </thead>
    //                                         <tbody>
    //                                             {
    //                                                 !clauses?.length || clauses?.length === 0 ? (
    //                                                     <tr>
    //                                                         <td colSpan="6" className="py-1 text-center">
    //                                                             No clause data available
    //                                                         </td>
    //                                                     </tr>
    //                                                 )
    //                                                     :
    //                                                     clauses?.map(
    //                                                         (clause, i) =>
    //                                                         (
    //                                                             <tr key={i} className="border-b border-gray-200">
    //                                                                 <td className="py-1 font-semibold text-gray-900 px-1">
    //                                                                     {clause?.ClauseType}
    //                                                                 </td>

    //                                                                 <td className="py-1 text-gray-700 px-1">
    //                                                                     {clause?.ModuleType}
    //                                                                 </td>

    //                                                                 <td className="py-1 text-center text-gray-700 px-1">
    //                                                                     {clause?.ClausesCategoryType}
    //                                                                 </td>

    //                                                                 <td className="py-1  text-gray-700 px-1">
    //                                                                     {clause?.ClauseDescription}
    //                                                                 </td>
    //                                                             </tr>
    //                                                         ),
    //                                                     )}
    //                                         </tbody>
    //                                     </table>
    //                                 </div>
    //                             </div>
    //                         </div>
    //                     </div>


    //                     {/* Customer Remarks */}
    //                     <div className="mb-3">
    //                         <h3 className="font-bold text-xs text-gray-900 uppercase mb-1">Customer Remarks:</h3>
    //                         <p className="text-[11px] text-gray-700 uppercase whitespace-pre-line leading-relaxed">
    //                             {formData?.customerRemarks || `BASED ON GENERAL CARGO.\nOCEANFREIGHT IS INCLUSIVE OF BUNKER, ISPS, TERMINAL, PORT, LOW SULFUR AND ENERGY SURCHARGE.`}
    //                         </p>
    //                         <h3 className="font-bold text-xs text-gray-900 uppercase mb-1 mt-1">Country Clause:</h3>
    //                         <p className="text-[11px] text-gray-700 uppercase whitespace-pre-line leading-relaxed">
    //                             {formData?.countryClause || "-"}
    //                         </p>
    //                     </div>

    //                     {/* Footer */}
    //                     <div className="border-t border-gray-300 pt-4 mt-6">
    //                         <div className="grid grid-cols-2 gap-4">
    //                             <div>
    //                                 <p className="text-xs text-gray-500 mb-1">Prepared By:</p>
    //                                 <p className="font-semibold text-gray-900 text-sm">{formData?.createdBy}</p>
    //                                 <p className="text-xs text-gray-600">Shipez AI</p>
    //                             </div>
    //                             <div className="text-right">
    //                                 <p className="text-xs text-gray-500 mb-1">Customer Acceptance:</p>
    //                                 <div className="border-b border-gray-400 w-40 h-6 ml-auto"></div>
    //                                 <p className="text-xs text-gray-500 mt-0.5">Signature & Date</p>
    //                             </div>
    //                         </div>

    //                         <div className="mt-4 pt-3 border-t border-gray-200 text-center">
    //                             <p className="text-xs text-gray-500">
    //                                 Thank you for choosing Shipez AI | www.shipez.ai
    //                             </p>
    //                         </div>
    //                     </div>
    //                 </div>
    //             </div>
    //         </div>

    //         {
    //             loader ?
    //                 <div className="min-h-screen w-screen absolute top-0 left-0 flex items-center justify-center bg-[rgba(0,0,0,0.6)]">
    //                     <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#0072FF]"></div>
    //                 </div>
    //                 : null
    //         }

    //         {/* Print Styles */}
    //         <style>
    //             {`
    //                 .a4-page {
    //                     width: 210mm;
    //                     min-height: 297mm;
    //                     box-sizing: border-box;
    //                 }

    //                 @media screen {
    //                     .a4-page {
    //                         margin: 0 auto;
    //                     }
    //                 }

    //                 @media print {
    //                     @page {
    //                         size: A4;
    //                         margin: 8mm;
    //                     }

    //                     html, body {
    //                         width: 210mm;
    //                         height: 297mm;
    //                     }

    //                       table,
    //                       tr,td,th{
    //                       border: none !important;
    //                       }

    //                     body {
    //                         margin: 0;
    //                         padding: 0;
    //                         print-color-adjust: exact;
    //                         -webkit-print-color-adjust: exact;
    //                     }

    //                     .a4-page {
    //                         width: 100%;
    //                         min-height: auto;
    //                         padding: 0;
    //                         margin: 0;
    //                         box-shadow: none !important;
    //                     }

    //                     .print\\:hidden {
    //                         display: none !important;
    //                     }

    //                     .print\\:py-0 {
    //                         padding-top: 0 !important;
    //                         padding-bottom: 0 !important;
    //                     }

    //                     .print\\:shadow-none {
    //                         box-shadow: none !important;
    //                     }

    //                     .print\\:bg-white {
    //                         background: white !important;
    //                     }
    //                 }
    //             `}
    //         </style>
    //     </div >
    // );
};

export default RateQuotePrint;